import * as echarts from "echarts";
import { useEffect, useRef } from "react";
import { RBI_COLORS, RBI_WEIGHTS } from "../../utils/brandIndexCalc";
import { ENTITY_LABELS } from "../../utils/labelMapping";
import classes from "./WeightedDonutChart.module.css";

const DEFAULT_DIVISORS = { visibility: 2, sentiment: 2, brandPillars: 10, purchaseCriteria: 10 };

/**
 * Weighted donut chart for Brand Index Score.
 *
 * Props:
 *   total         – final RBI integer (center label)
 *   visNorm       – normalized visibility score
 *   sentNorm      – normalized sentiment score
 *   pillarsNorm   – normalized brand pillars score
 *   purchaseNorm  – normalized purchase criteria score
 *   size          – "large" (default) | "small"
 *   weights       – { visibility, sentiment, brandPillars, purchaseCriteria } — defaults to
 *                    RBI_WEIGHTS. A segment is omitted entirely when its weight is falsy
 *                    (e.g. pass ACCESSIBILITY_RBI_WEIGHTS, which has no purchaseCriteria).
 *   colors        – same shape, defaults to RBI_COLORS
 *   divisors      – raw→normalized divisor per segment (tooltip "Raw Score" back-compute),
 *                    defaults to DEFAULT_DIVISORS
 *   outOf         – denominator shown under the center total (default 100)
 *   segments      – optional override, fully replaces the RBI-shaped segment computation
 *                    above (weights/colors/divisors/visNorm/etc. are ignored when this is
 *                    set). Used for multi-factor custom index scores (e.g. Accessibility),
 *                    where each slice is a *factor* rolling up its own Visibility/Sentiment/
 *                    Pillar sub-scores, not a single metric. Shape per entry:
 *                    { key, label, icon?, color, sliceValue, displayValue, weightPct,
 *                      sub: [{ label, raw, norm, max }] }
 */
export default function WeightedDonutChart({
  total = 0,
  visNorm = 0,
  sentNorm = 0,
  pillarsNorm = 0,
  purchaseNorm = 0,
  size = "large",
  showLegend = true,
  weights = RBI_WEIGHTS,
  colors = RBI_COLORS,
  divisors = DEFAULT_DIVISORS,
  outOf = 100,
  segments,
}) {
  const chartRef = useRef(null);
  const isSmall = size === "small";
  const isMedium = size === "medium";
  const useCustomSegments = Array.isArray(segments) && segments.length > 0;


  const SEGMENTS = useCustomSegments ? segments : [
    { key: "sentiment",        label: "Sentiment",                    icon: "😊" },
    { key: "purchaseCriteria", label: ENTITY_LABELS.purchaseCriteria,  icon: "🛒" },
    { key: "brandPillars",     label: ENTITY_LABELS.brandPillar,      icon: "🛡" },
    { key: "visibility",       label: ENTITY_LABELS.rank,              icon: "👁" },
  ]
    .filter((s) => weights[s.key])
    .map((s) => ({ ...s, weight: weights[s.key] }));

  // Legend shown below chart (same visual order as screenshot: Visibility, Sentiment, Brand Pillars, Purchase Criteria)
  const LEGEND_ORDER = useCustomSegments ? segments : [
    { key: "visibility",       label: ENTITY_LABELS.rank,              icon: "👁" },
    { key: "sentiment",        label: "Sentiment",                    icon: "😊" },
    { key: "brandPillars",     label: ENTITY_LABELS.brandPillar,      icon: "🛡" },
    { key: "purchaseCriteria", label: ENTITY_LABELS.purchaseCriteria,  icon: "🛒" },
  ]
    .filter((s) => weights[s.key])
    .map((s) => ({ ...s, weight: weights[s.key] }));

  const isReporting =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("isReporting") === "true"
      : false;

  const normalizedScores = {
    visibility: visNorm,
    sentiment: sentNorm,
    brandPillars: pillarsNorm,
    purchaseCriteria: purchaseNorm,
  };

  // Chart pixel dimensions (used for precise graphic text positioning)
  const chartHeightPx = isSmall ? 190 : isMedium ? 260 : 300;
  const scoreFontSize  = isSmall ? 28 : isMedium ? 32 : 46;

  const chartInstanceRef = useRef(null);

  useEffect(() => {
    if (!chartRef.current) return;
    const chart = echarts.init(chartRef.current);
    chartInstanceRef.current = chart;

    const pieData = SEGMENTS.map((seg) => {
      if (useCustomSegments) {
        return {
          name: seg.label,
          value: seg.sliceValue,
          itemStyle: { color: seg.color, borderWidth: 3, borderColor: "#ffffff" },
          label: {
            show: true,
            position: "inside",
            formatter: () => String(seg.displayValue),
            color: "#fff",
            fontSize: isSmall ? 9 : isMedium ? 11 : 15,
            fontWeight: "bold",
          },
          labelLine: { show: false },
        };
      }
      const normScore = normalizedScores[seg.key];
      const contribution = (normScore * seg.weight).toFixed(1);
      return {
        name: seg.label,
        value: seg.weight * 100,
        itemStyle: {
          color: colors[seg.key],
          borderWidth: 3,
          borderColor: "#ffffff",
        },
        label: {
          show: true,
          position: "inside",
          formatter: () => String(contribution),
          color: "#fff",
          fontSize: isSmall ? 9 : isMedium ? 11 : 15,
          fontWeight: "bold",
        },
        labelLine: { show: false },
      };
    });

    const option = {
      animation: !isReporting,
      animationDuration: 800,
      series: [
        {
          type: "pie",
          radius: isSmall ? ["30%", "75%"] : isMedium ? ["32%", "74%"] : ["35%", "74%"],
          center: ["50%", "50%"],
          startAngle: 90,   // start at 12 o'clock
          clockwise: true,
          data: pieData,
          emphasis: { disabled: true },
        },
      ],
      graphic: [
        // Total score in center
        {
          type: "text",
          left: "center",
          top: "middle",
          style: {
            text: String(total),
            textAlign: "center",
            fill: "#0a1f44",
            fontSize: scoreFontSize,
            fontWeight: "bold",
            fontFamily: "Inter, sans-serif",
          },
          z: 100,
        },
      ],
      tooltip: {
        trigger: "item",
        confine: true,
        formatter: (params) => {
          const seg = SEGMENTS.find((s) => s.label === params.name);
          if (!seg) return "";

          if (useCustomSegments) {
            const subRows = (seg.sub || [])
              .map(
                (s) =>
                  `${s.label}: <b>${s.raw != null ? s.raw : "—"}</b> → ${Math.round(s.norm)}/${s.max}<br/>`
              )
              .join("");
            return `
              <div style="padding:2px 4px">
                <b>${seg.label}</b><br/>
                ${subRows}
                Weight: ${seg.weightPct}%<br/>
                Factor Score: <b>${seg.displayValue}</b>
              </div>
            `;
          }

          const normScore = Math.round(normalizedScores[seg.key]);
          const contribution = (normScore * seg.weight).toFixed(1);
          // Back-compute raw score from normalized value
          const divisor = divisors[seg.key] ?? 1;
          const rawScore = (normScore / divisor).toFixed(divisor === 2 ? 0 : 2);
          return `
            <div style="padding:2px 4px">
              <b>${seg.label}</b><br/>
              Raw Score: <b>${rawScore}</b><br/>
              Normalized Score: ${normScore}<br/>
              Weight: ${Math.round(seg.weight * 100)}%<br/>
              Contribution: <b>${contribution}</b>
            </div>
          `;
        },
      },
    };

    chart.setOption(option);

    function handleTabChange() {
      setTimeout(() => {
        chart.setOption(option);
        chart.resize();
      });
    }

    function handlePrint() {
      const w = chartRef.current ? chartRef.current.offsetWidth : 0;
      chart.resize({ width: w > 0 ? w : undefined, height: chartHeightPx });
    }

    window.addEventListener("tabchange", handleTabChange);
    window.addEventListener("beforeprint", handlePrint);
    window.addEventListener("afterprint", handlePrint);

    return () => {
      chart.dispose();
      chartInstanceRef.current = null;
      window.removeEventListener("tabchange", handleTabChange);
      window.removeEventListener("beforeprint", handlePrint);
      window.removeEventListener("afterprint", handlePrint);
    };
  }, [total, visNorm, sentNorm, pillarsNorm, purchaseNorm, size, weights, colors, divisors, outOf, segments]);

  return (
    <div className={classes.wrapper}>
      <div ref={chartRef} style={{ width: "100%", height: `${chartHeightPx}px` }} />
      {showLegend && (
        <div className={`${classes.legend} ${isSmall ? classes.legend_compact : ""}`}>
          {LEGEND_ORDER.map((seg) => (
            <div key={seg.key} className={classes.legend_item}>
              <span
                className={classes.legend_dot}
                style={{ backgroundColor: useCustomSegments ? seg.color : colors[seg.key] }}
              />
              <span className={classes.legend_label}>{seg.label}</span>
              <span className={classes.legend_weight}>
                {useCustomSegments ? seg.weightPct : Math.round(seg.weight * 100)}%
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
